import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Table } from 'react-bootstrap';
import { IconCheck, IconMaximize, IconArrowLeft, IconPencil, IconPlus, IconRefresh, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../shared/api';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useConflictCheck } from '../../../shared/useConflictCheck';
import { InlineLoader } from '../../../components/LoadingSpinner';
import NumberInput from '../../../components/form/NumberInput';
import DateRangeField from '../../../components/form/DateRangeField';
import { useConfirm } from '../../../shared/ConfirmContext';
import CategoryFilterBoard from '../../warehouses/components/CategoryFilterBoard';
import './ProjectItems.css';

type Id = number | string;

interface ApiError extends Error {
  response?: { data?: Record<string, any> };
}

interface Category {
  id: Id;
  name: string;
  item_count?: number;
  color?: string | null;
  parent?: Id | null;
}

interface ProductRow {
  id: number;
  name: string;
  category_name?: string | null;
  tracking_type?: string | null;
  total_quantity_available?: number | string | null;
  default_tariff?: number | string | null;
}

interface ProjectEquipmentRow {
  id: number;
  product: number;
  product_name: string;
  quantity_requested: number;
  date_from?: string | null;
  date_to?: string | null;
  time_units: number | string;
  tariff?: number | string | null;
  applied_rate_type?: string | null;
  unit_price?: number | string | null;
  total_price?: number | string | null;
}

interface TariffOption {
  id: number;
  name: string;
  amount: number | string;
  tariff_type_details?: { name?: string | null } | null;
}

interface PendingChange {
  product: ProductRow;
  quantity?: number | string;
  timeUnits?: number | string;
  dateFrom?: string | null;
  dateTo?: string | null;
  tariff?: number | string | null;
  tariffLabel?: string | null;
  isNew?: boolean;
  isDeleted?: boolean;
}

interface ProjectEquipmentProps {
  projectId: Id;
  onDirtyChange?: (isDirty: boolean) => void;
  defaultTimeUnits?: number;
  projectStartDate?: string | null;
  projectEndDate?: string | null;
}

interface ScrollLockElement extends HTMLDivElement {
  _unlockScroll?: () => void;
}

interface CategoryResponse {
  categories?: Category[] | { results?: Category[] };
  results?: Category[];
}

const asList = <T,>(response: { results?: T[] } | T[] | undefined): T[] => {
  if (Array.isArray(response)) return response;
  return response?.results || [];
};

const asCategories = (response: CategoryResponse | Category[] | undefined): Category[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.categories)) return response.categories;
  if (response?.categories && Array.isArray(response.categories.results)) return response.categories.results;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

export default function ProjectEquipment({ projectId, onDirtyChange, defaultTimeUnits = 1, projectStartDate, projectEndDate }: ProjectEquipmentProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [currentCategory, setCurrentCategory] = useState<Id>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Draft workflow: quantity/tariff edits are staged here and only persisted on "Save".
  const [pendingChanges, setPendingChanges] = useState<Map<number, PendingChange>>(new Map());

  const hasUnsavedRef = useRef(false);
  const scrollableContainerRef = useRef<ScrollLockElement | null>(null);
  const categoryFiltersRef = useRef<HTMLDivElement | null>(null);
  const focusToolbarRef = useRef<HTMLDivElement | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const hintRef = useRef<HTMLDivElement | null>(null);
  const [hintHeight, setHintHeight] = useState(0);

  // Debounce search — wait 350ms after last keystroke before filtering the catalog
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: categories = [], isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ['project-equipment-product-categories'],
    queryFn: () => api.get<CategoryResponse | Category[]>('warehouses/api/products/categories_all/').then(asCategories),
  });

  // Keyboard shortcuts for quick category switching (ignored while typing in a field):
  //   ArrowLeft/ArrowRight — previous/next category (wraps), Escape — back to "all"
  useEffect(() => {
    if (!categories || categories.length === 0) return;
    const onKey = (event: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((document.activeElement as HTMLElement | null)?.isContentEditable) return;

      const ids = categories.map((c) => c.id);
      const idx = ids.indexOf(currentCategory);
      const base = idx === -1 ? -1 : idx;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setCurrentCategory(ids[(base + 1 + ids.length) % ids.length]);
        setIsFocusMode(true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setCurrentCategory(ids[(base - 1 + ids.length) % ids.length]);
        setIsFocusMode(true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (isFocusMode) {
          setCurrentCategory('all');
          setIsFocusMode(false);
          setTimeout(() => categoryFiltersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [categories, currentCategory, isFocusMode]);

  // Track focus toolbar height so thead can stick below it
  useEffect(() => {
    const el = focusToolbarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setToolbarHeight(el.offsetHeight));
    ro.observe(el);
    setToolbarHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [isFocusMode]);

  // Track hint bar height so the spacer matches exactly
  useEffect(() => {
    const el = hintRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHintHeight(el.offsetHeight));
    ro.observe(el);
    setHintHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [isFocusMode]);

  // Scroll the table into view on entering focus mode, then lock page scroll behind it
  useEffect(() => {
    const el = scrollableContainerRef.current;
    if (!el) return;

    if (isFocusMode) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const lockTimer = setTimeout(() => {
        const onWheel = (event: WheelEvent) => {
          const { scrollTop, scrollHeight, clientHeight } = el;
          const atTop = scrollTop === 0 && event.deltaY < 0;
          const atBottom = scrollTop + clientHeight >= scrollHeight && event.deltaY > 0;
          if (!atTop && !atBottom) {
            event.preventDefault();
            el.scrollTop += event.deltaY;
          }
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('wheel', onWheel, { passive: false });
        el._unlockScroll = () => {
          document.body.style.overflow = '';
          window.removeEventListener('wheel', onWheel);
        };
      }, 400);

      return () => {
        clearTimeout(lockTimer);
        if (el._unlockScroll) { el._unlockScroll(); delete el._unlockScroll; }
      };
    }
    document.body.style.overflow = '';
    if (el._unlockScroll) { el._unlockScroll(); delete el._unlockScroll; }
  }, [isFocusMode]);

  const equipmentKey = ['project-equipment', projectId];
  const { data: equipment = [] } = useQuery<ProjectEquipmentRow[]>({
    queryKey: equipmentKey,
    queryFn: () => api.get(`projects/api/project-equipment/?project=${projectId}`).then(asList<ProjectEquipmentRow>),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: equipmentKey });
  useRealtimeEvents((e) => {
    if (e.model !== 'projects.projectequipment') return;
    if (hasUnsavedRef.current) return; // don't clobber a staged-but-unsaved edit
    refresh();
  });
  const equipmentByProduct = useMemo(() => new Map(equipment.map((entry) => [entry.product, entry])), [equipment]);

  const editingExisting = editingProductId != null ? equipmentByProduct.get(editingProductId) : null;
  const editingPending = editingProductId != null ? pendingChanges.get(editingProductId) : null;
  const stockConflict = useConflictCheck('stock', {
    product: editingProductId,
    date_from: editingPending?.dateFrom || projectStartDate,
    date_to: editingPending?.dateTo || projectEndDate,
    quantity: editingPending?.quantity,
    exclude_equipment_id: editingExisting?.id,
    exclude_project_id: projectId,
  }, editingProductId != null);

  const { data: products = [], isLoading: productsLoading } = useQuery<ProductRow[]>({
    queryKey: ['project-equipment-catalog', currentCategory, debouncedSearchQuery],
    queryFn: () => {
      const params = new URLSearchParams({ page_size: '200', ordering: 'name' });
      if (currentCategory !== 'all') params.set('category', String(currentCategory));
      if (debouncedSearchQuery.trim()) params.set('search', debouncedSearchQuery.trim());
      return api.get(`warehouses/api/products/?${params.toString()}`).then(asList<ProductRow>);
    },
  });

  // Tariffs available for the product currently being edited, so the user can pick a rate type
  const { data: productTariffs = [] } = useQuery<TariffOption[]>({
    queryKey: ['product-tariffs', editingProductId],
    queryFn: () => api.get(`settings/api/tariffs/for_entity/?entity_app=warehouses&entity_type=product&entity_id=${editingProductId}`),
    enabled: !!editingProductId,
  });

  const addEquipment = useMutation<ProjectEquipmentRow, ApiError, { product: ProductRow; quantity: number | string; timeUnits: number | string; dateFrom?: string | null; dateTo?: string | null; tariff?: number | string | null }>({
    mutationFn: ({ product, quantity, timeUnits, dateFrom, dateTo, tariff }) =>
      api.post('projects/api/project-equipment/', { project: Number(projectId), product: product.id, quantity_requested: Number(quantity), time_units: Number(timeUnits), date_from: dateFrom || null, date_to: dateTo || null })
        .then(async (created) => {
          await api.post(`projects/api/project-equipment/${created.id}/auto-allocate/`, {});
          await api.post(`projects/api/project-equipment/${created.id}/recalculate-price/`, tariff ? { tariff_id: tariff } : {});
          return created;
        }),
    onError: (requestError) => setError(requestError?.response?.data?.tariff?.[0] || requestError?.response?.data?.error || t('projects.equipment.addError')),
  });

  const updateEquipment = useMutation<void, ApiError, { id: number; quantity: number | string; timeUnits: number | string; dateFrom?: string | null; dateTo?: string | null; tariff?: number | string | null }>({
    mutationFn: ({ id, quantity, timeUnits, dateFrom, dateTo, tariff }) =>
      api.patch(`projects/api/project-equipment/${id}/`, { quantity_requested: Number(quantity), time_units: Number(timeUnits), date_from: dateFrom || null, date_to: dateTo || null })
        .then(async () => {
          try { await api.post(`projects/api/project-equipment/${id}/auto-allocate/`, {}); } catch (_) { /* allocations already progressed — keep existing */ }
          await api.post(`projects/api/project-equipment/${id}/recalculate-price/`, { tariff_id: tariff, force: true });
        }),
    onError: (requestError) => setError(requestError?.response?.data?.error || t('projects.equipment.editError')),
  });

  const removeEquipment = useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api.delete(`projects/api/project-equipment/${id}/`),
    onError: () => setError(t('projects.equipment.removeError')),
  });

  const saveAll = useMutation<void, ApiError, void>({
    mutationFn: async () => {
      for (const [productId, change] of pendingChanges.entries()) {
        const existing = equipmentByProduct.get(productId);
        if (change.isDeleted) {
          if (existing) await removeEquipment.mutateAsync(existing.id);
          continue;
        }
        if (existing) {
          await updateEquipment.mutateAsync({ id: existing.id, quantity: change.quantity, timeUnits: change.timeUnits, dateFrom: change.dateFrom, dateTo: change.dateTo, tariff: change.tariff });
        } else {
          await addEquipment.mutateAsync({ product: change.product, quantity: change.quantity, timeUnits: change.timeUnits, dateFrom: change.dateFrom, dateTo: change.dateTo, tariff: change.tariff });
        }
      }
    },
    onSuccess: () => { setPendingChanges(new Map()); refresh(); },
    onError: (requestError: ApiError) => setError(requestError?.response?.data?.error || t('projects.equipment.saveError')),
  });

  const isMutating = saveAll.isPending;
  const hasUnsavedChanges = pendingChanges.size > 0;
  useEffect(() => onDirtyChange?.(isMutating || hasUnsavedChanges), [isMutating, hasUnsavedChanges, onDirtyChange]);
  useEffect(() => { hasUnsavedRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);

  // Every field edit writes straight into pendingChanges — there is no separate
  // "confirm" step. The inline editor (opened via stageAdd/startEdit) is just a
  // view over whatever is already staged for that product.
  const patchPending = (product: ProductRow, patch: Partial<PendingChange>) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      const existing = equipmentByProduct.get(product.id);
      const baseline: PendingChange = next.get(product.id) ?? {
        product,
        quantity: existing?.quantity_requested ?? 1,
        timeUnits: existing?.time_units ?? defaultTimeUnits,
        dateFrom: existing?.date_from ?? null,
        dateTo: existing?.date_to ?? null,
        tariff: existing?.tariff ?? product.default_tariff ?? null,
        tariffLabel: null,
        isNew: !existing,
        isDeleted: false,
      };
      next.set(product.id, { ...baseline, ...patch, isDeleted: false });
      return next;
    });
  };
  // Opening the editor for a product that isn't staged yet mirrors its saved values
  // (or sensible defaults for a brand-new one) into pendingChanges right away.
  const startEdit = (product: ProductRow) => {
    if (!pendingChanges.has(product.id)) {
      const existing = equipmentByProduct.get(product.id);
      const isBrandNew = !existing;
      patchPending(product, {
        quantity: existing?.quantity_requested ?? 1,
        timeUnits: existing?.time_units ?? defaultTimeUnits,
        dateFrom: existing?.date_from ?? (isBrandNew ? projectStartDate : null) ?? null,
        dateTo: existing?.date_to ?? (isBrandNew ? projectEndDate : null) ?? null,
        tariff: existing?.tariff ?? product.default_tariff ?? null,
        tariffLabel: null,
        isNew: isBrandNew,
      });
    }
    setEditingProductId(product.id);
  };
  const closeEdit = () => setEditingProductId(null);
  // Adding a product stages it into pendingChanges immediately (it counts toward the
  // batch right away) and opens it in the inline editor at the same time, highlighted
  // as pending, so quantity/dates/tariff can be tuned before hitting "Save changes".
  const stageAdd = (product: ProductRow) => {
    patchPending(product, {
      quantity: 1,
      timeUnits: defaultTimeUnits,
      dateFrom: projectStartDate ?? null,
      dateTo: projectEndDate ?? null,
      tariff: product.default_tariff ?? null,
      tariffLabel: null,
      isNew: true,
    });
    setEditingProductId(product.id);
  };
  const handleRemove = async (product: ProductRow) => {
    const existing = equipmentByProduct.get(product.id);
    if (!existing && !pendingChanges.has(product.id)) return;
    if (existing) {
      if (!await confirm(t('projects.equipment.confirmRemove'))) return;
      setPendingChanges((prev) => new Map(prev).set(product.id, { product, isDeleted: true }));
    } else {
      // Not saved yet — just drop the pending add.
      setPendingChanges((prev) => { const next = new Map(prev); next.delete(product.id); return next; });
    }
    if (editingProductId === product.id) closeEdit();
  };
  const handleUndoRemove = (productId: number) => setPendingChanges((prev) => { const next = new Map(prev); next.delete(productId); return next; });
  const handleDiscardAll = async () => {
    if (!await confirm(t('projects.equipment.confirmDiscard'))) return;
    setPendingChanges(new Map());
    closeEdit();
  };

  const searchBox = (
    <div className="item-browser-toolbar__search flex-grow-1">
      <Form onSubmit={(event) => event.preventDefault()}>
        <div className="input-group item-browser-toolbar__input-group">
          <span className="input-group-text"><IconSearch size={14} stroke={1.5} /></span>
          <Form.Control type="text" placeholder={t('projects.equipment.searchPlaceholder')} value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
        </div>
      </Form>
    </div>
  );

  const statusAndActions = (
    <div className="item-browser-toolbar__right" style={{ marginLeft: 0 }}>
      <div className={`item-browser-status-badge${hasUnsavedChanges ? ' item-browser-status-badge--dirty' : ''}`}>
        <span className="item-browser-status-badge__count">{equipment.length}</span>
        <span className="item-browser-status-badge__label">{t('projects.equipment.itemsInProject')}</span>
        {hasUnsavedChanges && <span className="item-browser-status-badge__pending">+{pendingChanges.size}</span>}
      </div>
      {hasUnsavedChanges && (
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleDiscardAll} title={t('projects.equipment.cancel')}>
          <IconX size={14} />
        </button>
      )}
      <button
        type="button"
        className={`btn btn-sm fw-medium${hasUnsavedChanges ? ' btn-warning' : ' btn-success'}`}
        onClick={() => saveAll.mutate()}
        disabled={!hasUnsavedChanges || saveAll.isPending}
      >
        <IconCheck size={14} className="me-1" />
        {hasUnsavedChanges ? `${t('projects.equipment.saveChanges')} (${pendingChanges.size})` : t('projects.equipment.saveChanges')}
      </button>
    </div>
  );

  const toolbar = (
    <div className="item-browser-toolbar">
      {searchBox}
      {statusAndActions}
      <div className="item-browser-toolbar__divider" />
      <button type="button" className="btn btn-sm btn-outline-secondary item-browser-toolbar__focus-btn" onClick={() => setIsFocusMode(true)} title={t('projects.equipment.fullscreen')}>
        <IconMaximize size={14} />
      </button>
    </div>
  );

  return <div className="d-grid gap-3 project-items">
    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

    <div className="item-browser-enhanced">
      <div ref={categoryFiltersRef}>
        <CategoryFilterBoard
          categories={categories}
          selectedCategory={currentCategory}
          onCategoryChange={(categoryId) => { setCurrentCategory(categoryId); setIsFocusMode(true); }}
          allLabel={t('projects.equipment.allCategories')}
        />
      </div>
      {categoriesLoading && <div className="text-center py-2"><InlineLoader /></div>}

      {!isFocusMode && <div className="mb-3">{toolbar}</div>}

      <div
        className="table-responsive"
        ref={scrollableContainerRef}
        style={isFocusMode ? {
          position: 'fixed', top: 0, left: 0, right: 0, bottom: hintHeight || '2.5rem', zIndex: 1050,
          background: 'var(--tblr-bg-surface-secondary, var(--tblr-bg-surface))',
          overflowY: 'auto', padding: '0 0.75rem',
        } : { maxHeight: '520px', overflowY: 'auto' }}
      >
        {isFocusMode && (
          <div ref={focusToolbarRef} style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--tblr-bg-surface, #fff)', borderBottom: '1px solid var(--tblr-border-color)', padding: '0.5rem 1.75rem', margin: '0 -0.75rem' }}>
            <div className="item-browser-toolbar item-browser-toolbar--focus">
              <button type="button" className="btn btn-sm btn-outline-secondary item-browser-toolbar__back-btn" onClick={() => { setCurrentCategory('all'); setIsFocusMode(false); setTimeout(() => categoryFiltersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50); }}>
                <IconArrowLeft size={14} style={{ verticalAlign: '-1px' }} />
                <span className="item-browser-toolbar__back-label">{t('projects.equipment.back')}</span>
              </button>
              {statusAndActions}
              <div className="item-browser-toolbar__divider" />
              {searchBox}
            </div>
          </div>
        )}

        <Table className="table table-hover table-vcenter mb-0">
          <thead className="sticky-top" style={{ background: 'var(--tblr-bg-surface, #fff)', top: isFocusMode ? toolbarHeight : 0 }}>
            <tr>
              <th style={{ minWidth: 180 }}>{t('projects.equipment.product')}</th>
              <th className="d-none d-md-table-cell" style={{ minWidth: 110 }}>{t('projects.equipment.category')}</th>
              <th style={{ minWidth: 80 }}>{t('projects.equipment.available')}</th>
              <th style={{ minWidth: 90 }}>{t('projects.equipment.quantity')}</th>
              <th className="d-none d-lg-table-cell" style={{ minWidth: 90 }}>{t('projects.equipment.units')}</th>
              <th className="d-none d-xl-table-cell" style={{ minWidth: 160 }}>{t('projects.equipment.dateRange', 'Termín')}</th>
              <th className="d-none d-lg-table-cell" style={{ minWidth: 160 }}>{t('projects.equipment.tariff')}</th>
              <th className="text-center" style={{ minWidth: 90 }}>{t('projects.equipment.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {productsLoading ? (
              <tr><td colSpan={8} className="text-center py-5"><InlineLoader /></td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-4 text-muted">{t('projects.equipment.noProductsMatch')}</td></tr>
            ) : products.map((product) => {
              const isConsumable = product.tracking_type === 'CONSUMABLE';
              const existing = equipmentByProduct.get(product.id);
              const pending = pendingChanges.get(product.id);
              const isPendingRemove = Boolean(pending?.isDeleted);
              const isInProject = isPendingRemove ? false : Boolean(existing) || Boolean(pending);
              const isEditing = editingProductId === product.id;
              const available = Number(product.total_quantity_available || 0);
              const requestedQty = isPendingRemove ? null : (pending?.quantity ?? existing?.quantity_requested ?? null);
              const requestedQuantity = requestedQty == null ? null : Number(requestedQty);
              const requestedUnits = isPendingRemove ? null : (pending?.timeUnits ?? existing?.time_units ?? null);
              const shortage = !isConsumable && requestedQuantity != null ? Math.max(0, requestedQuantity - available) : 0;

              return (
                <tr
                  key={product.id}
                  className={`item-row ${isInProject ? 'item-row--in-project' : ''} ${isEditing ? 'item-row--editing' : ''} ${pending ? 'item-row--pending' : ''}`}
                  onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest('button') || target.closest('input') || target.closest('select')) return;
                    if (isEditing || isPendingRemove) return;
                    if (isInProject) { startEdit(product); } else { stageAdd(product); }
                  }}
                  style={{ cursor: isEditing || isPendingRemove ? 'default' : 'pointer' }}
                >
                  <td>
                    <div className="d-flex align-items-center">
                      {isInProject && <IconCheck size={16} className="text-success me-2 flex-shrink-0" />}
                      <span className="fw-medium">{product.name}</span>
                      {pending && (
                        <Badge bg="warning" className="ms-2 text-dark">
                          {isPendingRemove ? t('projects.equipment.pendingRemove') : pending.isNew ? t('projects.equipment.pendingAdd') : t('projects.equipment.pendingEdit')}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="d-none d-md-table-cell">
                    <span className="badge">{product.category_name || '-'}</span>
                  </td>
                  <td>
                    {isConsumable ? <span className="text-muted fw-medium">∞</span> : (
                      <span className={`fw-medium ${available <= 0 ? 'text-danger' : available <= 5 ? 'text-warning' : 'text-success'}`}>{available}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <>
                        <NumberInput
                          size="sm" min={1} step="any" autoFocus
                          value={pending?.quantity ?? 1}
                          onChange={(quantity) => patchPending(product, { quantity })}
                        />
                        {shortage > 0 && <div className="text-warning small mt-1"><small>⚠ {t('projects.equipment.borrowWarning', { count: shortage })}</small></div>}
                      </>
                    ) : requestedQty != null ? (
                      <span className={`fw-medium ${pending ? 'text-warning' : 'text-primary'}`}>{requestedQty}</span>
                    ) : <span className="text-muted">-</span>}
                  </td>
                  <td className="d-none d-lg-table-cell">
                    {isEditing ? (
                      <NumberInput
                        size="sm" min={0.01} step="any"
                        value={pending?.timeUnits ?? defaultTimeUnits}
                        onChange={(timeUnits) => patchPending(product, { timeUnits })}
                      />
                    ) : requestedUnits != null ? (
                      <span className={`fw-medium ${pending ? 'text-warning' : 'text-primary'}`}>{requestedUnits}</span>
                    ) : <span className="text-muted">-</span>}
                  </td>
                  <td className="d-none d-xl-table-cell">
                    {isEditing ? (
                      <DateRangeField
                        dateFrom={pending?.dateFrom ?? null}
                        dateTo={pending?.dateTo ?? null}
                        projectStartDate={projectStartDate}
                        projectEndDate={projectEndDate}
                        onChange={({ dateFrom, dateTo }) => patchPending(product, { dateFrom, dateTo })}
                        warning={(stockConflict?.count ?? 0) > 0 && (
                          <div className="text-warning small">
                            <small>⚠ {t('projects.equipment.dateConflictWarning', { count: stockConflict?.count, defaultValue: 'Konflikt s {{count}} jiným projektem' })}</small>
                          </div>
                        )}
                      />
                    ) : (isPendingRemove ? (
                      <span className="text-muted">-</span>
                    ) : (existing?.date_from || existing?.date_to || pending?.dateFrom || pending?.dateTo) ? (
                      <small>{(pending?.dateFrom ?? existing?.date_from) || '…'} – {(pending?.dateTo ?? existing?.date_to) || '…'}</small>
                    ) : isInProject ? (
                      <small className="text-muted">{t('projects.equipment.wholeProject', 'Celý projekt')}</small>
                    ) : <span className="text-muted">-</span>)}
                  </td>
                  <td className="d-none d-lg-table-cell">
                    {isEditing ? (
                      <Form.Select
                        size="sm"
                        value={pending?.tariff || ''}
                        onChange={(event) => {
                          const value = event.target.value || null;
                          const chosen = productTariffs.find((tariff) => String(tariff.id) === String(value));
                          patchPending(product, { tariff: value, tariffLabel: chosen ? `${chosen.name} (${chosen.tariff_type_details?.name}, ${chosen.amount})` : null });
                        }}
                      >
                        <option value="">{t('projects.equipment.defaultTariff')}</option>
                        {productTariffs.map((tariff) => (
                          <option key={tariff.id} value={tariff.id}>{tariff.name} ({tariff.tariff_type_details?.name}, {tariff.amount})</option>
                        ))}
                      </Form.Select>
                    ) : isPendingRemove ? (
                      <span className="text-muted">-</span>
                    ) : pending ? (
                      <span className="text-warning">{pending.tariffLabel || t('projects.equipment.pendingLabel')}</span>
                    ) : existing ? (
                      <span className="text-primary">{existing.applied_rate_type || t('projects.equipment.notSet')} · {existing.unit_price} Kč</span>
                    ) : <span className="text-muted">-</span>}
                  </td>
                  <td className="text-center">
                    {isEditing ? (
                      <Button size="sm" variant="outline-secondary" onClick={(event) => { event.stopPropagation(); closeEdit(); }} title={t('projects.equipment.close')}>
                        <IconX size={14} />
                      </Button>
                    ) : isPendingRemove ? (
                      <Button size="sm" variant="outline-secondary" onClick={(event) => { event.stopPropagation(); handleUndoRemove(product.id); }} title={t('projects.equipment.undo')}>
                        <IconRefresh size={14} />
                      </Button>
                    ) : isInProject ? (
                      <div className="btn-group">
                        <Button size="sm" variant="outline-info" onClick={(event) => { event.stopPropagation(); startEdit(product); }} title={t('projects.equipment.edit')}>
                          <IconPencil size={14} />
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={(event) => { event.stopPropagation(); handleRemove(product); }} title={t('projects.equipment.removeFromProject')}>
                          <IconTrash size={14} />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="primary" onClick={(event) => { event.stopPropagation(); stageAdd(product); }}>
                        <IconPlus size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Keyboard hint — fixed at bottom of fullscreen overlay */}
      {isFocusMode && (
        <div ref={hintRef} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1051, textAlign: 'center', padding: '0.35rem', background: 'var(--tblr-bg-surface, #fff)', borderTop: '1px solid var(--tblr-border-color)' }}>
          <span className="category-filter-hint" style={{ opacity: 0.5 }}>
            <kbd>←</kbd> <kbd>→</kbd> {t('projects.items.categoryHint')}
          </span>
        </div>
      )}
    </div>
  </div>;
}
